import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  FiHelpCircle,
  FiPhone,
  FiMail,
  FiMapPin,
  FiSend,
  FiChevronDown,
  FiChevronUp,
  FiCheckCircle,
  FiMessageSquare,
  FiClock,
} from "react-icons/fi";

const FAQS = [
  {
    id: 1,
    question: "How do I contact a property owner or listing agent?",
    answer:
      "Navigate to any property or listing detail page. You will find dedicated 'Call' and 'Chat' options to directly connect with the verified owner or agent.",
  },
  {
    id: 2,
    question: "How do I update my profile and contact preferences?",
    answer:
      "Go to your Account settings in the top right menu, then click 'Edit Profile'. You can update your phone number, email, address, and city location.",
  },
  {
    id: 3,
    question: "How do Reward Points and Loyalty Cash work?",
    answer:
      "You earn points by interacting with properties, inviting contacts, verifying your details, and sharing promotions. Once you reach the minimum balance, points can be redeemed towards subscription benefits.",
  },
  {
    id: 4,
    question: "How can I join or create a Neighborhood Community?",
    answer:
      "Visit the 'Communities' section from the left navigation. You can explore public neighborhood communities and join them instantly, or click 'Create Community' to build your own local circle.",
  },
  {
    id: 5,
    question: "How do I book or inquire about Home Services?",
    answer:
      "Explore the 'Services Directory' tab to discover verified architects, contractors, interior designers, and service providers. Click any service to view photos, prices, and schedule a meeting reminder.",
  },
];

const MAX_CHARS = 500;

export default function CustomerHelpSupport() {
  const authState = useSelector((state) => state.auth);
  const user = authState?.user?.userDetails || authState?.user || {};

  const [expandedFaq, setExpandedFaq] = useState(1);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const handleToggleFaq = (id) => {
    setExpandedFaq((prev) => (prev === id ? null : id));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    if (!message.trim()) {
      setErrorMsg("Please enter your support message.");
      return;
    }
    if (message.trim().length < 10) {
      setErrorMsg("Please provide more details (minimum 10 characters).");
      return;
    }

    setSubmitting(true);
    // Simulates inquiry dispatch matching mobile Help.js
    setTimeout(() => {
      setSubmitting(false);
      setSuccessMsg("Thank you! Your inquiry has been sent to our customer support team. We will get back to you shortly.");
      setMessage("");
      setSubject("");
    }, 1200);
  };

  return (
    <div className="container-fluid px-3 px-md-4 py-3">
      {/* Hero Header */}
      <div
        className="card border-0 rounded-4 shadow-sm mb-4 text-white overflow-hidden p-4 p-md-5"
        style={{
          background: "linear-gradient(135deg, #074da3 0%, #1e40af 100%)",
        }}
      >
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-4">
          <div>
            <span className="badge bg-white bg-opacity-25 text-white mb-2 px-3 py-1 fw-normal">
              24/7 Dedicated Support
            </span>
            <h2 className="fw-bold mb-2">Help & Customer Support Hub</h2>
            <p className="mb-0 text-white text-opacity-75" style={{ maxWidth: "580px" }}>
              Have questions about your properties, rewards, local communities, or services? We're here to assist you at every step.
            </p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <a
              href="tel:+919818961783"
              className="btn btn-light d-flex align-items-center gap-2 fw-semibold px-3 py-2 shadow-sm text-primary"
            >
              <FiPhone /> Call +91 9818961783
            </a>
            <a
              href="mailto:support@allhomedesire.com"
              className="btn btn-outline-light d-flex align-items-center gap-2 fw-semibold px-3 py-2"
            >
              <FiMail /> support@allhomedesire.com
            </a>
          </div>
        </div>
      </div>

      <div className="row g-4">
        {/* Left Column: FAQs Accordion */}
        <div className="col-12 col-lg-7">
          <div className="card border-0 shadow-sm rounded-3 p-4 h-100">
            <h5 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
              <FiHelpCircle className="text-primary" /> Frequently Asked Questions
            </h5>
            <p className="text-muted small mb-4">
              Quick answers to the most common questions regarding platform features.
            </p>

            <div className="d-flex flex-column gap-3">
              {FAQS.map((faq) => {
                const isOpen = expandedFaq === faq.id;
                return (
                  <div
                    key={faq.id}
                    className={`border rounded-3 p-3 transition-all cursor-pointer ${
                      isOpen ? "border-primary bg-primary bg-opacity-10 shadow-sm" : "bg-white"
                    }`}
                    onClick={() => handleToggleFaq(faq.id)}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <span className={`fw-semibold ${isOpen ? "text-primary" : "text-dark"}`}>
                        {faq.question}
                      </span>
                      {isOpen ? (
                        <FiChevronUp className="text-primary flex-shrink-0 ms-2" />
                      ) : (
                        <FiChevronDown className="text-muted flex-shrink-0 ms-2" />
                      )}
                    </div>
                    {isOpen && (
                      <div className="mt-2 pt-2 border-top border-primary border-opacity-25 text-secondary small">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Column: Send Inquiry Form */}
        <div className="col-12 col-lg-5">
          <div className="card border-0 shadow-sm rounded-3 p-4 h-100">
            <h5 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
              <FiMessageSquare className="text-primary" /> Send an Inquiry
            </h5>
            <p className="text-muted small mb-3">
              Fill out the form below and our team will get back to you within 24 hours.
            </p>

            {successMsg && (
              <div className="alert alert-success d-flex align-items-center gap-2 small py-2 mb-3">
                <FiCheckCircle size={16} />
                <span>{successMsg}</span>
              </div>
            )}

            {errorMsg && (
              <div className="alert alert-danger small py-2 mb-3">
                {errorMsg}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label fw-semibold small text-dark">Subject / Topic</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. Question about Reward Points or Property verification"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </div>

              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <label className="form-label fw-semibold small text-dark mb-0">
                    Message Details <span className="text-danger">*</span>
                  </label>
                  <span className="text-muted" style={{ fontSize: "11px" }}>
                    {message.length}/{MAX_CHARS}
                  </span>
                </div>
                <textarea
                  className="form-control"
                  rows="5"
                  placeholder="Describe your issue or question in detail..."
                  value={message}
                  onChange={(e) => {
                    if (e.target.value.length <= MAX_CHARS) {
                      setMessage(e.target.value);
                    }
                  }}
                  required
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2 shadow-sm py-2"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" />
                    Sending Inquiry...
                  </>
                ) : (
                  <>
                    <FiSend size={15} /> Send Message
                  </>
                )}
              </button>
            </form>

            <div className="border-top mt-4 pt-3 text-muted small">
              <div className="d-flex align-items-center gap-2 mb-1">
                <FiClock className="text-primary" size={14} />
                <span>Working Hours: Mon - Sat (9:00 AM - 7:00 PM IST)</span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <FiMapPin className="text-danger" size={14} />
                <span>All Home Desire Headquarters, India</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
